import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  AppointmentProfileEntry,
  ApptLocation,
} from '@/types/settings-api';
import { appointmentProfileListQueryKey } from './use-appointment-profile-list';

/**
 * Save-body shape for a single appointment-profile entry. Extends the
 * GET-response shape (`AppointmentProfileEntry`) with two save-only
 * fields verified against the captured dev cURL 2026-05-20:
 *
 *   - `id`: `null` on Add rows; the existing GUID on Edit rows.
 *     The GET-side type has `id: string` because every persisted entry
 *     has one, but on the wire for an Add submission the new row ships
 *     `id: null` and the backend allocates.
 *   - `referenceId`: present on every save entry. For Edit rows it
 *     mirrors `id` (`referenceId === id`); for Add rows it's `null`.
 *     Flutter parity (`appointment_profile_model.dart:60-174`) —
 *     CreateAppointmentProfileRequest has both `id` and `referenceId`,
 *     where `referenceId` is the client-side temp handle the bloc
 *     uses for in-flight reconciliation. We don't reconcile in React
 *     (React Query handles that), but the backend reads the field;
 *     omitting it causes Guid-parser failures.
 *
 * Cross-reference: `AppointmentProfileEntry` (GET-response) does NOT
 * include `referenceId`. Don't conflate — read uses the strict type,
 * write uses this looser save type.
 */
export interface SaveAppointmentProfileEntry
  extends Omit<AppointmentProfileEntry, 'id'> {
  id: string | null;
  referenceId: string | null;
}

/**
 * Save-body envelope shape for the POST. Same outer wrapper as
 * `AppointmentProfileEnvelope` but with the save-shape entries.
 *
 * `isDeleted: string[]` carries GUIDs of profile rows to archive.
 * Empty array `[]` when nothing was deleted; populated on Delete.
 *
 * `enabledLocation` lists the locations available on this practice
 * (verbatim from the GET-side `enabledLocation` echo) — backend reads
 * to validate the per-row `location` references.
 */
export interface SaveAppointmentProfileBody {
  createAppointmentProfileRequest: SaveAppointmentProfileEntry[];
  isDeleted: string[];
  enabledLocation: ApptLocation[];
}

/**
 * Save the practitioner's appointment-profile list (PROD-762, final
 * PROD-762 editor — completes the epic).
 *
 *   POST /api/Doctor/AppointmentProfile
 *
 * **FIVE magic-string / shape traps captured from the dev cURL 2026-05-20**:
 *
 *   1. Envelope key is `createAppointmentProfileRequest` —
 *      THIRD distinct wrapper key on the Schedule tab. Distinct from:
 *        - `request` (AppointmentTemplate POST)
 *        - bare array (CalendarSettings POST)
 *      Pin negative-asserts both alternatives in tests.
 *
 *   2. **`practionerId` on the body is the TYPO'd spelling** (verbatim).
 *      OPPOSITE of `AppointmentTemplateEntry.practitionerId` which is
 *      correctly spelled on the body. Same Schedule tab — different
 *      parent record uses different spelling. Pin negative-asserts
 *      `practitionerId` (correct) is NOT on the body.
 *
 *   3. **Color format is Flutter `Color(int)` ARGB hex** (e.g.
 *      `"0xFFA7CAFF"`). NOT a CSS hex like `"#A7CAFF"`. First two
 *      digits after `0x` are alpha. Display layer converts to CSS;
 *      write layer must reverse the conversion before save.
 *
 *   4. **`id: null` on Add rows**, GUID on Edit rows. The
 *      GET-response type marks `id` as `string` because every persisted
 *      row has one, but the save body permits `null` for new rows so
 *      the backend allocates. Distinct from Working Hours (which uses
 *      `id: ""`).
 *
 *   5. **`referenceId` is required on EVERY save entry**, but is NOT
 *      in the GET response. Mirrors `id` on Edit rows
 *      (`referenceId === id`); `null` on Add rows. Backend reads it
 *      for Guid-parser purposes; omitting causes 400/500.
 *
 * Additional contracts:
 *   - Nested envelopes (`location`, `speciality.code`, `reasonCode`,
 *     `appointmentType`, optional `billingProfile`) ship populated;
 *     they are NOT collapsed to bare GUIDs.
 *   - `enabledLocation: ApptLocation[]` ships verbatim from the
 *     GET-side echo (don't auto-derive).
 *   - List-snapshot semantics: editor edits ONE row but submits the
 *     full practitioner list; `isUpdated: true` on the changed row,
 *     `false` on preserved rows. Same convention as
 *     `useSaveCalendarSettings` and `useSaveAppointmentTemplate`.
 *
 * Flutter parity: `EnableProviderScheduleEvent` dispatch from
 * `settings_data_source_impl.dart` save handler.
 */
export function useSaveAppointmentProfile(
  doctorSetupId: string,
  organizationId: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveAppointmentProfileBody) => {
      return post<unknown>('/api/Doctor/AppointmentProfile', body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: appointmentProfileListQueryKey(doctorSetupId, organizationId),
      });
    },
  });
}
